const sanitizeInterpretationText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const normalizeComparableInterpretation = (value: string): string =>
  value.replace(/\s+/g, ' ').trim();

const getPayloadRecord = (payload: unknown): Record<string, unknown> | null =>
  payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;

const getReportRecord = (payload: unknown): Record<string, unknown> | null => {
  const record = getPayloadRecord(payload);
  if (!record) return null;
  const report = record.report;
  return report && typeof report === 'object' ? { ...(report as Record<string, unknown>) } : null;
};

export const getAssessmentInterpretationOverride = (payload: unknown): string =>
  sanitizeInterpretationText(getReportRecord(payload)?.interpretation);

export const getAssessmentInterpretationDraft = (
  payload: unknown,
  automaticInterpretation?: string | null,
): string => {
  const stored = getAssessmentInterpretationOverride(payload);
  if (!stored) return '';

  const automatic = sanitizeInterpretationText(automaticInterpretation);
  if (
    automatic &&
    normalizeComparableInterpretation(stored) === normalizeComparableInterpretation(automatic)
  ) {
    return '';
  }

  return stored;
};

export const resolveAssessmentInterpretationText = (
  payload: unknown,
  automaticInterpretation?: string | null,
): string => {
  const override = getAssessmentInterpretationOverride(payload);
  if (override) return override;

  const record = getPayloadRecord(payload);
  const directInterpretation = sanitizeInterpretationText(record?.interpretation);
  if (directInterpretation) return directInterpretation;

  return sanitizeInterpretationText(automaticInterpretation);
};

export const withAssessmentInterpretationOverride = <T>(
  payload: T,
  interpretation: unknown,
): T => {
  const record = getPayloadRecord(payload);
  if (!record) return payload;

  const nextInterpretation = sanitizeInterpretationText(interpretation);
  const nextReport = getReportRecord(payload) ?? {};

  if (nextInterpretation) {
    nextReport.interpretation = nextInterpretation;
    return {
      ...record,
      report: nextReport,
    } as T;
  }

  delete nextReport.interpretation;
  if (Object.keys(nextReport).length > 0) {
    return {
      ...record,
      report: nextReport,
    } as T;
  }

  const { report: _report, ...rest } = record;
  return rest as T;
};

export const splitAssessmentInterpretationLines = (interpretation: string): string[] =>
  interpretation
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
